/**
 * NEXT50 — Purchase-option DataLayer enrichment (Option 1)
 *
 * Keeps Shopify item id/name intact. Adds:
 *   - purchase_option: single_subscription | double_subscription | buy_once
 *   - item_variant / variant: same label (GA4-friendly)
 *   - selling_plan_id: Shopify selling plan id when applicable
 *
 * Enriches gtag-style pushes: ['event', 'view_item'|'add_to_cart'|..., payload]
 * and object-style pushes: { event, ecommerce: { items: [...] } }
 */
(() => {
  if (window.__n50PurchaseOptionDl) return;
  window.__n50PurchaseOptionDl = true;

  const EVENTS = new Set(['view_item', 'add_to_cart', 'begin_checkout', 'purchase']);
  const cfg = window.__N50_PURCHASE_OPTION__ || {};
  const planIdMap = cfg.planIdMap || {};

  /** @type {{ data: any, t: number } | null} */
  let cartCache = null;
  /** @type {string | null} */
  let lastAddPurchaseOption = null;
  /** @type {string | number | null} */
  let lastAddSellingPlanId = null;

  function normalizePlanName(name) {
    return String(name || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function purchaseOptionFromPlan(sellingPlanId, sellingPlanName) {
    if (sellingPlanId != null && sellingPlanId !== '' && planIdMap[String(sellingPlanId)]) {
      return planIdMap[String(sellingPlanId)];
    }
    const name = normalizePlanName(sellingPlanName);
    if (!sellingPlanId && !name) return 'buy_once';
    if (name.includes('double')) return 'double_subscription';
    if (name.includes('single')) return 'single_subscription';
    if (name.includes('buy once') || name.includes('one-time') || name.includes('onetime')) {
      return 'buy_once';
    }
    if (!sellingPlanId) return 'buy_once';
    return 'single_subscription';
  }

  function purchaseOptionFromPlanKey(key) {
    if (key === 'subscribe-a') return 'single_subscription';
    if (key === 'subscribe-b') return 'double_subscription';
    if (key === 'onetime') return 'buy_once';
    return null;
  }

  function selectedPdpPurchaseOption() {
    const selected = document.querySelector(
      '[data-n50-plan-option] .n50-pdp-buy__radio:checked, [data-n50-plan-option].is-selected'
    );
    const option = selected?.closest?.('[data-n50-plan-option]') || selected;
    if (!option) return null;
    const fromAttr = option.getAttribute('data-purchase-option');
    if (fromAttr) return fromAttr;
    return purchaseOptionFromPlanKey(option.getAttribute('data-plan-key'));
  }

  function selectedPdpSellingPlanId() {
    const input = document.querySelector('[data-n50-selling-plan]');
    if (input && !input.disabled && input.value) return input.value;
    const option =
      document
        .querySelector('[data-n50-plan-option] .n50-pdp-buy__radio:checked')
        ?.closest('[data-n50-plan-option]') || null;
    return option?.getAttribute('data-selling-plan') || '';
  }

  async function getCart(force) {
    if (!force && cartCache && Date.now() - cartCache.t < 2500) return cartCache.data;
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return cartCache?.data || null;
      const data = await res.json();
      cartCache = { data, t: Date.now() };
      return data;
    } catch {
      return cartCache?.data || null;
    }
  }

  function parseShopifyItemId(id) {
    const parts = String(id || '').split('_');
    if (parts.length >= 4 && parts[0] === 'shopify') {
      return { productId: parts[2], variantId: parts[3] };
    }
    return { productId: null, variantId: String(id || '') };
  }

  function findCartLineForItem(cart, item) {
    if (!cart?.items?.length) return null;
    const parsed = parseShopifyItemId(item.item_id || item.id);
    const variantId = String(item.item_variant_id || item.variant_id || parsed.variantId || '');
    const productId = String(item.item_id_product || parsed.productId || '');

    return (
      cart.items.find((line) => {
        if (variantId && String(line.variant_id) === variantId) return true;
        if (productId && String(line.product_id) === productId) return true;
        return false;
      }) || cart.items[0]
    );
  }

  function enrichItem(item, meta) {
    if (!item || typeof item !== 'object') return item;
    const purchaseOption = meta.purchaseOption;
    const sellingPlanId = meta.sellingPlanId;

    if (purchaseOption) {
      if ('item_variant' in item || 'item_name' in item || 'item_id' in item) {
        item.item_variant = purchaseOption;
      }
      if ('variant' in item || 'name' in item || 'id' in item) {
        item.variant = purchaseOption;
      }
      item.purchase_option = purchaseOption;
    }
    if (sellingPlanId) {
      item.selling_plan_id = String(sellingPlanId);
    } else if (purchaseOption === 'buy_once') {
      item.selling_plan_id = '';
    }
    return item;
  }

  function extractItemsFromPayload(payload) {
    if (!payload || typeof payload !== 'object') return null;
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.ecommerce?.items)) return payload.ecommerce.items;
    if (Array.isArray(payload.ecommerce?.add?.products)) return payload.ecommerce.add.products;
    if (Array.isArray(payload.ecommerce?.purchase?.products)) return payload.ecommerce.purchase.products;
    if (Array.isArray(payload.ecommerce?.checkout?.products)) return payload.ecommerce.checkout.products;
    if (Array.isArray(payload.ecommerce?.detail?.products)) return payload.ecommerce.detail.products;
    return null;
  }

  /**
   * GTM/gtag often calls dataLayer.push(arguments) — an Arguments object,
   * not a real Array. Normalize so we can read event name + payload.
   */
  function normalizePushArgs(rawArgs) {
    const list = Array.prototype.slice.call(rawArgs || []);
    if (list.length === 1) {
      const only = list[0];
      if (Array.isArray(only)) return only;
      // Arguments / array-like: ['event', name, payload]
      if (
        only &&
        typeof only === 'object' &&
        typeof only.length === 'number' &&
        only[0] === 'event' &&
        typeof only[1] === 'string'
      ) {
        return Array.prototype.slice.call(only);
      }
    }
    return list;
  }

  function eventNameFromArgs(args) {
    if (!args?.length) return null;
    const first = args[0];
    if (typeof first === 'string' && first === 'event' && typeof args[1] === 'string') {
      return args[1];
    }
    if (first && typeof first === 'object') {
      return first.event || first.eventName || null;
    }
    return null;
  }

  function payloadFromArgs(args) {
    if (!args?.length) return null;
    if (typeof args[0] === 'string' && args[0] === 'event') return args[2] || null;
    if (args[0] && typeof args[0] === 'object') return args[0];
    return null;
  }

  async function resolveMetaForItems(eventName, items) {
    const cart = await getCart(eventName === 'add_to_cart' || eventName === 'begin_checkout');
    const pdpOption = selectedPdpPurchaseOption();
    const pdpPlanId = selectedPdpSellingPlanId();

    return (items || []).map((item) => {
      const line = findCartLineForItem(cart, item);
      const props = line?.properties || {};
      const propOption = props.purchase_option || props._purchase_option || null;
      const allocation = line?.selling_plan_allocation?.selling_plan;
      const linePlanId = allocation?.id || '';
      const linePlanName = allocation?.name || '';

      let purchaseOption =
        propOption ||
        (line ? purchaseOptionFromPlan(linePlanId, linePlanName) : null) ||
        lastAddPurchaseOption ||
        pdpOption ||
        'buy_once';

      let sellingPlanId =
        linePlanId ||
        lastAddSellingPlanId ||
        (purchaseOption === pdpOption ? pdpPlanId : '') ||
        '';

      if (purchaseOption === 'buy_once') sellingPlanId = '';

      // view_item: prefer currently selected PDP card when present
      if (eventName === 'view_item' && pdpOption) {
        purchaseOption = pdpOption;
        sellingPlanId = pdpPlanId || '';
      }

      return { purchaseOption, sellingPlanId };
    });
  }

  async function enrichArgs(args) {
    const eventName = eventNameFromArgs(args);
    if (!eventName || !EVENTS.has(eventName)) return args;

    const payload = payloadFromArgs(args);
    const items = extractItemsFromPayload(payload);
    if (!items?.length) return args;

    const metas = await resolveMetaForItems(eventName, items);
    items.forEach((item, i) => enrichItem(item, metas[i] || {}));

    if (payload && typeof payload === 'object') {
      payload.purchase_option = metas[0]?.purchaseOption || payload.purchase_option;
      if (metas[0]?.sellingPlanId) payload.selling_plan_id = String(metas[0].sellingPlanId);
    }

    return args;
  }

  function enrichPushArgs(args) {
    const eventName = eventNameFromArgs(args);
    if (!eventName || !EVENTS.has(eventName)) return;
    const payload = payloadFromArgs(args);
    const items = extractItemsFromPayload(payload);
    if (!items?.length) return;

    // PDP default card is Single subscription; avoid buy_once when view_item
    // fires before plan radios exist in the DOM.
    const fallbackOption =
      Object.keys(planIdMap).length > 0 ? 'single_subscription' : 'buy_once';
    const pdpOption = selectedPdpPurchaseOption() || lastAddPurchaseOption;
    const pdpPlanId = selectedPdpSellingPlanId() || lastAddSellingPlanId || '';
    const cart = cartCache?.data;
    items.forEach((item) => {
      const line = findCartLineForItem(cart, item);
      const props = line?.properties || {};
      const allocation = line?.selling_plan_allocation?.selling_plan;
      let purchaseOption =
        props.purchase_option ||
        props._purchase_option ||
        (line ? purchaseOptionFromPlan(allocation?.id, allocation?.name) : null) ||
        pdpOption ||
        fallbackOption;
      let sellingPlanId = allocation?.id || (purchaseOption === pdpOption ? pdpPlanId : '') || '';
      if (eventName === 'view_item') {
        purchaseOption = selectedPdpPurchaseOption() || pdpOption || fallbackOption;
        sellingPlanId =
          selectedPdpSellingPlanId() ||
          (purchaseOption !== 'buy_once'
            ? Object.keys(planIdMap).find((id) => planIdMap[id] === purchaseOption) || ''
            : '');
      }
      if (purchaseOption === 'buy_once') sellingPlanId = '';
      enrichItem(item, { purchaseOption, sellingPlanId });
    });
    if (payload) {
      payload.purchase_option = items[0]?.purchase_option;
      payload.selling_plan_id = items[0]?.selling_plan_id || '';
    }
    getCart(true);
  }

  /**
   * Always sit outermost on dataLayer.push.
   * GTM replaces push after load; without re-wrapping, enrichment never runs.
   */
  function wrapDataLayer(dl) {
    if (!dl) return dl;
    // Already outermost with our enricher.
    if (typeof dl.push === 'function' && dl.push.__n50Enricher) return dl;

    const previousPush = dl.push.bind(dl);
    function n50EnrichedPush() {
      try {
        enrichPushArgs(normalizePushArgs(arguments));
      } catch (e) {
        /* never block analytics */
      }
      return previousPush.apply(dl, arguments);
    }
    n50EnrichedPush.__n50Enricher = true;
    dl.push = n50EnrichedPush;
    dl.__n50PurchaseOptionWrapped = true;
    return dl;
  }

  function ensureOutermostWrap() {
    try {
      const dl = window.dataLayer;
      if (!dl) return;
      wrapDataLayer(dl);
    } catch {
      /* ignore */
    }
  }

  // Ensure dataLayer exists and is wrapped before GTM / Shopify pixels push.
  window.dataLayer = wrapDataLayer(window.dataLayer || []);

  // Recreate wrap if something replaces dataLayer later.
  try {
    let current = window.dataLayer;
    Object.defineProperty(window, 'dataLayer', {
      configurable: true,
      enumerable: true,
      get() {
        return current;
      },
      set(value) {
        current = wrapDataLayer(value || []);
      },
    });
  } catch {
    /* defineProperty may fail in some contexts; push wrap still applied */
  }

  // Reclaim outermost push after GTM / Google channel wrap it.
  ensureOutermostWrap();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensureOutermostWrap, { once: true });
  }
  window.addEventListener('load', ensureOutermostWrap, { once: true });
  let rewrapAttempts = 0;
  const rewrapTimer = window.setInterval(() => {
    ensureOutermostWrap();
    rewrapAttempts += 1;
    if (rewrapAttempts >= 20) window.clearInterval(rewrapTimer);
  }, 250);

  // Capture selling plan from /cart/add.js payloads for race-free ATC enrichment.
  try {
    const originalFetch = window.fetch.bind(window);
    window.fetch = async function n50Fetch(input, init) {
      const url = typeof input === 'string' ? input : input?.url || '';
      if (/\/cart\/add\.js/.test(url) && init?.body) {
        try {
          let body = init.body;
          if (typeof body === 'string') {
            let parsed = null;
            try {
              parsed = JSON.parse(body);
            } catch {
              const params = new URLSearchParams(body);
              parsed = Object.fromEntries(params.entries());
              if (params.get('properties[_purchase_option]') || params.get('properties[purchase_option]')) {
                lastAddPurchaseOption =
                  params.get('properties[_purchase_option]') || params.get('properties[purchase_option]');
              }
            }
            if (parsed) {
              const item = parsed.items?.[0] || parsed;
              const plan = item.selling_plan || parsed.selling_plan || '';
              const prop =
                item.properties?._purchase_option ||
                item.properties?.purchase_option ||
                parsed.properties?._purchase_option ||
                parsed.properties?.purchase_option ||
                lastAddPurchaseOption;
              lastAddSellingPlanId = plan || '';
              lastAddPurchaseOption =
                prop || purchaseOptionFromPlan(plan, '') || selectedPdpPurchaseOption() || 'buy_once';
            }
          }
        } catch {
          /* ignore */
        }
      }
      if (/\/cart\/(add|change|update|clear)\.js/.test(url)) {
        cartCache = null;
      }
      return originalFetch(input, init);
    };
  } catch {
    /* fetch wrap optional */
  }

  // Keep last selected PDP option in sync for view_item / ATC races.
  document.addEventListener(
    'change',
    (event) => {
      const option = event.target?.closest?.('[data-n50-plan-option]');
      if (!option) return;
      lastAddPurchaseOption =
        option.getAttribute('data-purchase-option') ||
        purchaseOptionFromPlanKey(option.getAttribute('data-plan-key'));
      lastAddSellingPlanId = option.getAttribute('data-selling-plan') || '';
    },
    true
  );

  // Expose for debugging / GTM Preview
  window.N50PurchaseOption = {
    fromPlan: purchaseOptionFromPlan,
    selected: selectedPdpPurchaseOption,
    enrichArgs,
    getCart,
  };
})();
